import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import PhotoUploader from "@/components/PhotoUploader";
import WhatsAppButton from "@/components/WhatsAppButton";
import { BeforeAfterSlider } from "@/components/BeforeAfterSlider";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Smartphone, Loader2, Play, Square, MapPin, ArrowLeft, Camera } from "lucide-react";
import { toast } from "sonner";

export default function FieldMode() {
  const [appts, setAppts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sel, setSel] = useState(null);
  const [clients, setClients] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const today = (await api.get("/dashboard/today")).data.appointments || [];
      setAppts(today);
      const cls = (await api.get("/clients")).data || [];
      setClients(Object.fromEntries(cls.map((c) => [c.id, c])));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-emerald-600" /></div>;
  if (sel) return <FieldDetail appt={sel} client={clients[sel.client_id]} onBack={() => { setSel(null); load(); }} />;

  return (
    <div className="max-w-md mx-auto">
      <PageHeader title="Mode Terrain" subtitle="Vos interventions du jour, à portée de pouce" icon={Smartphone} />
      {appts.length === 0 ? <div className="bg-white border border-slate-200 rounded-xl p-10 text-center text-slate-500">Aucune intervention aujourd'hui.</div> :
        <div className="space-y-3">{appts.map((a) => (
          <button key={a.id} onClick={() => setSel(a)} data-testid={`field-appt-${a.id}`} className="w-full text-left bg-white border border-slate-200 rounded-xl p-4 hover:border-emerald-300">
            <div className="text-lg font-bold text-slate-900">{(a.start || "").slice(11, 16)} · {a.client_name || a.title || "Intervention"}</div>
            <div className="text-xs text-slate-400">{a.status}</div>
          </button>
        ))}</div>}
    </div>
  );
}

function FieldDetail({ appt, client, onBack }) {
  const [running, setRunning] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const timer = useRef(null);
  const [before, setBefore] = useState(appt.before_photos || []);
  const [after, setAfter] = useState(appt.after_photos || []);
  const [notes, setNotes] = useState(appt.notes || "");
  const [sign, setSign] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => () => clearInterval(timer.current), []);
  const start = () => { setRunning(true); timer.current = setInterval(() => setElapsed((e) => e + 1), 1000); };
  const stop = () => { setRunning(false); clearInterval(timer.current); };
  const fmt = (s) => `${String(Math.floor(s / 3600)).padStart(2, "0")}:${String(Math.floor((s % 3600) / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

  const persist = async (extra = {}) => {
    setSaving(true);
    try {
      await api.put(`/appointments/${appt.id}`, {
        ...appt, start: appt.start, end: appt.end,
        before_photos: before, after_photos: after, notes, ...extra,
      });
      toast.success("Enregistré");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };
  const finish = async () => {
    await persist();
    try {
      await api.post("/intervention-reports", {
        client_id: appt.client_id, appointment_id: appt.id, vehicle_id: appt.vehicle_id,
        work: appt.title, details: notes, hours: Math.round((elapsed / 3600) * 100) / 100,
        photos: [...before, ...after], client_signature: sign, status: "termine",
      });
      toast.success("Bon d'intervention créé");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    onBack();
  };

  return (
    <div className="max-w-md mx-auto space-y-4">
      <button onClick={onBack} className="flex items-center gap-1 text-sm text-slate-500"><ArrowLeft className="w-4 h-4" /> Retour</button>
      <div className="bg-white border border-slate-200 rounded-xl p-4">
        <div className="text-xl font-bold text-slate-900">{appt.client_name || appt.title}</div>
        {appt.location_address && <div className="text-sm text-slate-500 flex items-center gap-1 mt-1"><MapPin className="w-4 h-4" /> {appt.location_address}</div>}
        {client?.phone && <div className="mt-3 flex gap-2"><a href={`tel:${client.phone}`} className="text-sm px-3 py-1.5 rounded-lg bg-slate-100 text-slate-700">Appeler</a><WhatsAppButton phone={client.phone} context={{ client: appt.client_name, entreprise: "" }} /></div>}
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-4 text-center">
        <div className="text-4xl font-mono font-bold text-slate-900 tabular-nums" data-testid="field-timer">{fmt(elapsed)}</div>
        <div className="flex gap-2 justify-center mt-3">
          {!running ? <Button onClick={start} data-testid="field-start" className="bg-emerald-500 hover:bg-emerald-600 text-black font-semibold"><Play className="w-4 h-4 mr-1.5" /> Démarrer</Button>
            : <Button onClick={stop} data-testid="field-stop" variant="outline" className="border-red-500/30 text-red-600"><Square className="w-4 h-4 mr-1.5" /> Pause</Button>}
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-3">
        <div className="flex items-center gap-2 text-sm font-semibold text-slate-800"><Camera className="w-4 h-4" /> Photos</div>
        <PhotoUploader label="Avant" value={before} onChange={setBefore} testid="field-before" />
        <PhotoUploader label="Après" value={after} onChange={setAfter} testid="field-after" />
        {before[0] && after[0] && <BeforeAfterSlider before={before[0]} after={after[0]} height={220} />}
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-2">
        <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="field-notes" placeholder="Notes d'intervention…" className="bg-slate-50 border-slate-200" />
        <Input value={sign} onChange={(e) => setSign(e.target.value)} data-testid="field-signature" placeholder="Signature client (nom)" className="bg-slate-50 border-slate-200" />
      </div>

      <div className="flex gap-2">
        <Button onClick={() => persist()} disabled={saving} variant="outline" className="flex-1 border-slate-200">Enregistrer</Button>
        <Button onClick={finish} disabled={saving} data-testid="field-finish" className="flex-1 bg-slate-900 text-white hover:bg-slate-800">Terminer</Button>
      </div>
    </div>
  );
}
